import React from 'react';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';

import { server} from './settings'
import { Router } from './pages/router'
import { Apollo, isPageTeardownNetworkError } from './apollo'
import { gql, useQuery } from '@apollo/client';


import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { StoreProvider } from './store/storeProvider';
import { AuthProvider } from './store/authenticationProvider';
import { UserProvider } from './store/userProvider';
import { useAuthenticationState } from './store/useAuthenticationState';
import { useUser } from './store/useUser';
import { UserSecurityProvider } from './store/userSecurityProvider';
import { useUserSecurityState } from './store/useUserSecurityState';


import './css';


const GET_CURRENT_USER_DATA = gql`
  query GetCurrentUserData {
    getCurrentUserData {
      id
      username
      realname
      policies
      loginType
      settings {
        colorTheme
        dashboard
        adminSettingsFavorites
        systemSettingsFavorites
        centralPlannerPreferences {
          fullView
          focusColumns
          fullColumns
        }
        ecmDashboardColumnPreferences {
          page
          visibleColumns
        }
        recentParticipants {
          participantId
          odrNumber
          name
          visitedAt
        }
      }
    }
  }
`;


const UserBootstrap: React.FC = () => {
  const { authenticationState, shouldBootstrapUser } = useAuthenticationState();
  const { user, setUser, setIsUserLoading } = useUser();
  const { setSecurity, setIsSecurityLoading } = useUserSecurityState();

  const hasAuthenticatedUser = Boolean(user?.authenticated);
  const shouldFetchUser = Boolean(authenticationState) && shouldBootstrapUser && !hasAuthenticatedUser;
  const { loading } = useQuery(GET_CURRENT_USER_DATA, {
    skip: !shouldFetchUser,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (!data?.getCurrentUserData) {
        setUser({});
        if (setSecurity) {
          setSecurity({});
        }
        return;
      }

      setUser(data.getCurrentUserData);
      console.log('user policies', data.getCurrentUserData.policies || []);
      if (setSecurity) {
        setSecurity(data.getCurrentUserData);
      }
    },
    onError: (error) => {
      if (isPageTeardownNetworkError(error)) {
        return;
      }
      console.error('Error fetching user data:', error);
      setIsUserLoading(false);
      if (setIsSecurityLoading) {
        setIsSecurityLoading(false);
      }
    }
  });

  React.useEffect(() => {
    if (!authenticationState) {
      setUser({});
      setIsUserLoading(false);
      if (setSecurity) {
        setSecurity({});
      }
      if (setIsSecurityLoading) {
        setIsSecurityLoading(false);
      }
      return;
    }

    setIsUserLoading(loading);
    if (setIsSecurityLoading) {
      setIsSecurityLoading(loading);
    }
  }, [authenticationState, loading, setIsUserLoading, setUser, setSecurity, setIsSecurityLoading]);

  return null;
};


const ApolloWrapper = ()=> {
  const { authenticationState } = useAuthenticationState();
  
  return (
    <div className="App">
      <Apollo server= {server} auth={authenticationState}>           
        <UserSecurityProvider>
          <UserProvider>
            <StoreProvider>
                <UserBootstrap />
                <Router/>     
            </StoreProvider>
          </UserProvider>
        </UserSecurityProvider>
        <ToastContainer />
      </Apollo>     
    </div>
  );
}


export const App = () => {
	return (		
      <AuthProvider>
			  <ApolloWrapper />      		
      </AuthProvider>
	);
};

// Below identifies the root window. No need to change anything. 
//**************************************************************
//**************************************************************
//**************************************************************
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
